import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { QrCode, Camera, MagnifyingGlass, CheckCircle, XCircle, Spinner, SealCheck } from '@phosphor-icons/react';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScanPage = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [mode, setMode] = useState('manual'); // manual, camera
  const [certificateId, setCertificateId] = useState('');
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment', width: 640, height: 480 } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setStream(mediaStream);
      setMode('camera');
    } catch (error) {
      toast.error('Camera access denied');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setMode('manual');
  };

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const handleManualSearch = async (e) => {
    e.preventDefault();
    if (!certificateId.trim()) {
      toast.error('Please enter a certificate ID or number');
      return;
    }
    await verifyProduct(certificateId.trim());
  };

  const verifyProduct = async (certId) => {
    setLoading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('certificate_id', certId);

      const response = await axios.post(`${API}/scan/verify`, formData);
      setResult(response.data);
      
      if (response.data.verified) {
        toast.success('Product verified!');
      } else {
        toast.error('Product not found');
      }
    } catch (error) {
      setResult({ verified: false, message: 'Verification failed' });
      toast.error('Verification failed');
    } finally {
      setLoading(false);
    }
  };

  // Simulated QR scan (in production, use a QR scanning library)
  const handleCameraScan = () => {
    // Simulate finding a QR code
    toast.info('QR scanning in progress... (Demo mode)');
    setTimeout(() => {
      stopCamera();
      // For demo, use a sample certificate ID
      verifyProduct('GSG-');
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-8">
            <Badge variant="outline" className="mb-4">
              <QrCode weight="duotone" className="w-4 h-4 mr-2" />
              Product Verification
            </Badge>
            <h1 className="text-3xl md:text-4xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Scan & Verify
            </h1>
            <p className="text-muted-foreground">
              Verify the authenticity and sustainability score of any GinsGood certified product
            </p>
          </div>

          <Card className="p-6" data-testid="scan-card">
            <Tabs value={mode} onValueChange={(val) => { if(val === 'camera') startCamera(); else stopCamera(); }}>
              <TabsList className="grid grid-cols-2 mb-6">
                <TabsTrigger value="manual" className="gap-2">
                  <MagnifyingGlass weight="duotone" className="w-4 h-4" />
                  Enter ID
                </TabsTrigger>
                <TabsTrigger value="camera" className="gap-2">
                  <Camera weight="duotone" className="w-4 h-4" />
                  Scan QR
                </TabsTrigger>
              </TabsList>

              <TabsContent value="manual">
                <form onSubmit={handleManualSearch} className="space-y-4">
                  <div>
                    <Input
                      value={certificateId}
                      onChange={(e) => setCertificateId(e.target.value)}
                      placeholder="Enter Certificate ID or Number (e.g., GSG-20260115-ABC123)"
                      className="h-12"
                      data-testid="certificate-id-input"
                    />
                  </div>
                  <Button type="submit" className="w-full h-12 rounded-full gap-2" disabled={loading}>
                    {loading ? <Spinner className="w-5 h-5 animate-spin" /> : <MagnifyingGlass weight="bold" className="w-5 h-5" />}
                    Verify Product
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="camera">
                <div className="space-y-4">
                  <div className="relative aspect-video bg-black rounded-xl overflow-hidden">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover"
                    />
                    <canvas ref={canvasRef} className="hidden" />
                    {/* Scan overlay */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-48 h-48 border-2 border-white/50 rounded-xl">
                        <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-[#4A6C4C] rounded-tl-lg" />
                        <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-[#4A6C4C] rounded-tr-lg" />
                        <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-[#4A6C4C] rounded-bl-lg" />
                        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-[#4A6C4C] rounded-br-lg" />
                      </div>
                    </div>
                  </div>
                  <p className="text-center text-sm text-muted-foreground">
                    Point camera at product QR code
                  </p>
                  <div className="flex gap-4">
                    <Button variant="outline" onClick={stopCamera} className="flex-1">
                      Cancel
                    </Button>
                    <Button onClick={handleCameraScan} className="flex-1 gap-2">
                      <QrCode weight="bold" className="w-5 h-5" />
                      Capture
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            {/* Results */}
            {result && (
              <div className="mt-6 pt-6 border-t">
                {result.verified ? (
                  <div className="text-center space-y-4">
                    <div className="w-20 h-20 mx-auto bg-[#4A6C4C]/10 rounded-full flex items-center justify-center">
                      <CheckCircle weight="duotone" className="w-10 h-10 text-[#4A6C4C]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-medium text-[#4A6C4C]">Verified Product</h3>
                      <p className="text-muted-foreground">{result.message}</p>
                    </div>
                    
                    <Card className="p-4 text-left bg-secondary/50">
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Product</span>
                          <span className="font-medium">{result.product_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Certificate</span>
                          <span className="font-mono">{result.certificate_number}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">GinsGood Score</span>
                          <span className="font-bold text-[#4A6C4C]">{result.ginsgood_score}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Artisan</span>
                          <span>{result.artisan}, {result.location}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Status</span>
                          <Badge variant="secondary" className="bg-[#4A6C4C] text-white">
                            {result.status}
                          </Badge>
                        </div>
                      </div>
                    </Card>

                    <Button 
                      onClick={() => navigate(result.passport_url)} 
                      className="w-full gap-2"
                    >
                      <SealCheck weight="bold" className="w-5 h-5" />
                      View Full Passport
                    </Button>
                  </div>
                ) : (
                  <div className="text-center space-y-4">
                    <div className="w-20 h-20 mx-auto bg-destructive/10 rounded-full flex items-center justify-center">
                      <XCircle weight="duotone" className="w-10 h-10 text-destructive" />
                    </div>
                    <div>
                      <h3 className="text-xl font-medium text-destructive">Not Verified</h3>
                      <p className="text-muted-foreground">{result.message}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Quick Links */}
          <div className="mt-8 grid grid-cols-2 gap-4">
            <Button variant="outline" onClick={() => navigate('/iris-auth')} className="h-12">
              Register Identity
            </Button>
            <Button variant="outline" onClick={() => navigate('/certify')} className="h-12">
              Certify Product
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ScanPage;
